import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, CheckCircle, Star, Award, Mic2, Users, Building2, Loader } from "lucide-react";
import { toast } from "sonner";
import api from "../utils/api";
import CTABand from "../components/shared/CTABand";

function RevealSection({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

const TIERS = [
  {
    id: "title",
    name: "Title Sponsor",
    availability: "1 Exclusive Opportunity",
    icon: Award,
    color: "#00D4FF",
    featured: true,
    description: "The highest-impact partnership available. Your brand becomes synonymous with the event.",
    perks: [
      "Name in event title — 'Core AI Summit, presented by [Company]'",
      "Keynote speaking slot (main stage)",
      "40'×40' premium exhibition space",
      "20 attendee tickets included",
      "Full-page in all event materials",
      "AI Concierge integration + logo placement",
      "Dedicated meeting room for hosted sessions",
      "Press announcement co-authored",
      "All Silver/Gold/Platinum benefits included",
    ],
  },
  {
    id: "platinum",
    name: "Platinum Sponsor",
    availability: "2 Opportunities",
    icon: Star,
    color: "#7B2FBE",
    featured: false,
    description: "Mainstage visibility with premium speaking access and exhibition presence.",
    perks: [
      "Keynote or mainstage speaking opportunity",
      "20'×20' premium exhibition space",
      "10 attendee tickets",
      "Sponsored lunch session",
      "Logo on event stage backdrop",
      "Featured partner page on website",
      "Curated 1:1 meeting access",
    ],
  },
  {
    id: "gold",
    name: "Gold Sponsor",
    availability: "5 Opportunities",
    icon: Mic2,
    color: "#00D4FF",
    featured: false,
    description: "Breakout visibility, speaking access, and a meaningful exhibition presence.",
    perks: [
      "Breakout or panel speaking slot",
      "10'×20' exhibition space",
      "6 attendee tickets",
      "Logo on event collateral",
      "Partner page on website",
      "Networking reception inclusion",
    ],
  },
  {
    id: "silver",
    name: "Silver Sponsor",
    availability: "10 Opportunities",
    icon: Building2,
    color: "#00D4FF",
    featured: false,
    description: "Brand presence, exhibitor access, and direct engagement with attendees.",
    perks: [
      "10'×10' standard booth",
      "3 attendee tickets",
      "Logo on website & materials",
      "Exhibitor directory listing",
      "Lead retrieval app access",
    ],
  },
];

/* ── Inquiry Form ──────────────────────────────────── */
function PartnerInquiryForm({ initialType = "sponsorship" }) {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    inquiry_type: initialType, message: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      toast.success("We received your inquiry — we'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-12" data-testid="partner-inquiry-success">
        <CheckCircle size={40} className="mx-auto mb-4" style={{ color: "var(--cyan)" }} />
        <h3 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "var(--font-heading)" }}>
          We've got your details.
        </h3>
        <p style={{ color: "var(--text-muted)" }}>
          Our partnerships team will reach out within 2 business days with full package details and custom options.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="partner-inquiry-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Full Name *</label>
          <input name="name" value={form.name} onChange={handleChange} required placeholder="Jane Smith" className="input-dark" data-testid="partner-name-input" />
        </div>
        <div>
          <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Work Email *</label>
          <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="jane@company.com" className="input-dark" data-testid="partner-email-input" />
        </div>
        <div>
          <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Company *</label>
          <input name="company" value={form.company} onChange={handleChange} required placeholder="Your Company" className="input-dark" data-testid="partner-company-input" />
        </div>
        <div>
          <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Phone</label>
          <input name="phone" value={form.phone} onChange={handleChange} placeholder="+1 (555) 000-0000" className="input-dark" data-testid="partner-phone-input" />
        </div>
      </div>
      <div>
        <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Partnership Interest</label>
        <select name="inquiry_type" value={form.inquiry_type} onChange={handleChange} className="input-dark" data-testid="partner-type-select">
          <option value="title_sponsor">Title Sponsor — Presented By</option>
          <option value="platinum_sponsor">Platinum Sponsor</option>
          <option value="gold_sponsor">Gold Sponsor</option>
          <option value="silver_sponsor">Silver Sponsor</option>
          <option value="premium_booth">Premium Exhibitor Booth</option>
          <option value="standard_booth">Standard Exhibitor Booth</option>
          <option value="startup_pod">Startup Pod</option>
          <option value="general">General Partnership Inquiry</option>
        </select>
      </div>
      <div>
        <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Tell us about your goals</label>
        <textarea name="message" value={form.message} onChange={handleChange} rows={3}
          placeholder="What are your key objectives for partnering with Core AI Summit?" className="input-dark resize-none" data-testid="partner-message-input" />
      </div>
      <button type="submit" disabled={loading || !form.name || !form.email || !form.company}
        className="btn-primary" data-testid="partner-submit-btn"
        style={{ opacity: loading || (!form.name || !form.email || !form.company) ? 0.65 : 1 }}>
        {loading ? <><Loader size={14} className="animate-spin" /> Sending...</> : <>Send My Details <ArrowRight size={14} /></>}
      </button>
      <p className="text-xs" style={{ color: "var(--text-muted)", opacity: 0.7 }}>
        No commitments. We'll send you full package details and answer any questions.
      </p>
    </form>
  );
}

export default function PartnerPage() {
  const formRef = useRef(null);

  const scrollToForm = (tierId) => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="partner-page">
      {/* Hero */}
      <section className="page-hero" style={{ backgroundColor: "var(--surface)" }} data-testid="partner-hero">
        <div className="container-xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="overline-label mb-4">Partner with Core AI Summit</div>
            <h1 className="text-5xl sm:text-6xl font-black text-white mb-6 max-w-3xl"
              style={{ fontFamily: "var(--font-heading)" }}>Get in front of who matters.</h1>
            <p className="text-lg max-w-2xl" style={{ color: "var(--text-muted)" }}>
              Sponsoring Core AI Summit isn't about logo visibility. It's about meaningful business outcomes — curated meetings, speaking opportunities, and direct access to 1,500+ AI decision-makers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Audience Stats */}
      <section style={{ backgroundColor: "var(--bg)", borderBottom: "1px solid var(--border)" }}>
        <div className="container-xl py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "1,500+", label: "Attendees" },
              { value: "40%", label: "Enterprise / C-Suite" },
              { value: "30%", label: "Developers & Builders" },
              { value: "30%", label: "SMB Leaders" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-4xl font-black text-white mb-1" style={{ fontFamily: "var(--font-heading)" }}>{stat.value}</div>
                <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsorship Tiers */}
      <section className="section-py" data-testid="sponsor-tiers-section">
        <div className="container-xl">
          <RevealSection>
            <div className="max-w-2xl mb-16">
              <div className="overline-label mb-4">Sponsorship Packages</div>
              <h2 className="text-4xl font-black text-white mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                A tier for every business objective.
              </h2>
              <p style={{ color: "var(--text-muted)" }}>
                Send us your details and we'll follow up with full package information tailored to your goals.
              </p>
            </div>
          </RevealSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {TIERS.map((tier, i) => {
              const Icon = tier.icon;
              return (
                <RevealSection key={tier.id} delay={i * 0.1}>
                  <div
                    className={`sponsor-card relative overflow-hidden h-full flex flex-col ${tier.featured ? "border-[#00D4FF]/40" : ""}`}
                    style={{ background: tier.featured ? "linear-gradient(135deg, rgba(0,212,255,0.05), rgba(123,47,190,0.05))" : "var(--surface)" }}
                    data-testid={`sponsor-tier-${tier.id}`}
                  >
                    {tier.featured && (
                      <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: "linear-gradient(90deg, var(--cyan), var(--violet))" }} />
                    )}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ background: `${tier.color}18`, border: `1px solid ${tier.color}33` }}>
                        <Icon size={18} style={{ color: tier.color }} />
                      </div>
                      <div>
                        <div className="font-black text-xl text-white" style={{ fontFamily: "var(--font-heading)" }}>{tier.name}</div>
                        <div className="text-xs" style={{ color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>{tier.availability}</div>
                      </div>
                    </div>
                    <p className="text-sm mb-5" style={{ color: "var(--text-muted)" }}>{tier.description}</p>
                    <ul className="space-y-2.5 mb-6 flex-1">
                      {tier.perks.map((perk) => (
                        <li key={perk} className="flex items-start gap-2.5 text-sm">
                          <CheckCircle size={13} className="flex-shrink-0 mt-0.5" style={{ color: "var(--cyan)" }} />
                          <span style={{ color: "var(--text-muted)" }}>{perk}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => scrollToForm(tier.id)}
                      className="btn-outline w-full justify-center"
                      data-testid={`tier-inquire-btn-${tier.id}`}
                    >
                      Request Partnership Details <ArrowRight size={13} />
                    </button>
                  </div>
                </RevealSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="inquire" ref={formRef} className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="partner-inquiry-section">
        <div className="container-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <RevealSection>
              <div>
                <div className="overline-label mb-4">Get in Touch</div>
                <h2 className="text-4xl font-black text-white mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                  Let's build something meaningful together.
                </h2>
                <p className="text-base leading-relaxed mb-8" style={{ color: "var(--text-muted)" }}>
                  Share your details and our partnerships team will follow up with full package information, audience demographics, and custom options built around your objectives.
                </p>
                <div className="space-y-4">
                  {[
                    { label: "Outcome-driven packages", desc: "Built around leads, meetings, and brand authority" },
                    { label: "Custom options available", desc: "We'll design a partnership that fits your exact objectives" },
                    { label: "Founding sponsor advantage", desc: "Early partners get preferred positioning and priority placement" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: "var(--cyan)" }} />
                      <div>
                        <div className="text-sm font-semibold text-white">{item.label}</div>
                        <div className="text-sm" style={{ color: "var(--text-muted)" }}>{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </RevealSection>
            <RevealSection delay={0.15}>
              <div className="p-8 rounded-2xl" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                <h3 className="text-xl font-black text-white mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                  Partnership Inquiry
                </h3>
                <PartnerInquiryForm />
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      <CTABand
        title="Join as an Attendee"
        subtitle="Not ready to sponsor? Join 1,500+ AI leaders as an attendee."
        primaryLabel="Join Priority List"
        secondaryLabel="View Expo Options"
        secondaryHref="/expo"
      />
    </div>
  );
}
